import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Tapware",
  "Showers",
  "Bathtubs",
  "Toilets",
  "Basins",
  "Sinks",
  "Vanities",
  "LED Mirrors",
  "Accessories",
  "Trade / Wholesale",
  "Becoming a stockist",
  "Other",
];

const FIELD =
  "w-full rounded-2xl border border-ink/10 bg-sand/60 px-5 py-4 text-sm text-ink placeholder:text-ink/40 outline-none transition-colors duration-300 focus:border-ink/40 focus:bg-white";

const Contact = () => {
  const [searchParams] = useSearchParams();
  const interestParam = searchParams.get("interest");
  const productParam = searchParams.get("product");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: INTERESTS.includes(interestParam) ? interestParam : "Tapware",
    message: productParam
      ? `I'm interested in the ${productParam} — please send pricing and availability.`
      : "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", interest: "Tapware", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto max-w-[1500px] px-6 pb-32 pt-32 md:px-10 md:pt-44" data-testid="contact-page">
      <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
        <div className="md:col-span-5">
          <FadeUp>
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50">
              Contact &amp; trade
            </p>
          </FadeUp>
          <h1 className="mt-5 font-display text-5xl font-medium leading-[1.05] tracking-tight text-ink md:text-7xl" data-testid="contact-headline">
            <MaskedLine delay={0.15}>Start a</MaskedLine>
            <MaskedLine delay={0.27}>conversation.</MaskedLine>
          </h1>
          <FadeUp delay={0.5} className="mt-8 max-w-md">
            <p className="text-base leading-relaxed text-ink/70 md:text-lg">
              Product questions, trade pricing, or a full bathroom schedule — tell us what
              you're building and we'll come back with answers, not brochures.
            </p>
          </FadeUp>
          <FadeUp delay={0.6} className="mt-12 space-y-5">
            {[
              { l: "Email", v: "sales@sancub.com" },
              { l: "Phone", v: "1300 680 580" },
              { l: "Address", v: "35 Apex Drive, Truganina VIC 3029" },
              { l: "Web", v: "sancub.com" },
              { l: "Hours", v: "Mon–Fri 9:00–5:30 · Sat–Sun 9:00–4:30 AEST" },
            ].map((r) => (
              <div key={r.l} className="flex flex-col gap-1 border-b border-ink/5 pb-5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
                <span className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink/50">{r.l}</span>
                <span className="text-sm font-semibold text-ink">{r.v}</span>
              </div>
            ))}
          </FadeUp>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <FadeUp delay={0.3}>
            <form onSubmit={submit} className="space-y-5 rounded-[2rem] bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-12" data-testid="enquiry-form">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="enquiry-name" className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/50">Name *</label>
                  <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={FIELD} />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/50">Email *</label>
                  <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={FIELD} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="enquiry-phone" className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/50">Phone</label>
                  <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="Optional" className={FIELD} />
                </div>
                <div>
                  <label htmlFor="enquiry-interest" className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/50">I'm interested in</label>
                  <select id="enquiry-interest" data-testid="enquiry-interest-select" value={form.interest} onChange={set("interest")} className={FIELD}>
                    {INTERESTS.map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="enquiry-message" className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/50">Message *</label>
                <textarea id="enquiry-message" data-testid="enquiry-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your project…" className={FIELD} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit-btn"
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-ink px-8 py-4.5 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.01] hover:bg-ink/90 disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <>
                    Send enquiry
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </main>
  );
};

export default Contact;
